import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Check, Lock, Mail, Eye, EyeOff, ArrowRight, ArrowLeft,
  ShieldCheck, Star, Headphones, PiggyBank, Sparkles, Zap, UserPlus,
} from 'lucide-react';
import { signInWithGoogle, signInWithApple, registerWithEmail } from '../../lib/firebase';
import { redirectToCheckout } from '../../lib/stripe';

/* ── Plan data ─────────────────────────────────────────────────────────── */
const PLANS = {
  free: {
    key: 'free', tag: 'Free account', tagClass: 'free',
    name: 'Daily Report', price: '$0', sub: '/ always free',
    note: 'Register once. Read the report every market day.',
    nav: { name: 'Free', price: 'Always free', best: false },
    paid: false,
    features: [
      { type: 'on',     text: 'Full daily written report' },
      { type: 'on',     text: 'Market analysis & news-impact coverage' },
      { type: 'on',     text: 'Projection outcomes — Hit / Beat / Miss' },
      { type: 'locked', text: 'Projection ranges & target levels' },
      { type: 'locked', text: 'Bull & bear case + Devil’s Advocate' },
    ],
  },
  monthly: {
    key: 'monthly', tag: 'Pro · Monthly', tagClass: 'pro-mo',
    name: 'Investment Intelligence Daily Pro', price: '$14.95', sub: '/ month',
    note: 'Cancel anytime.',
    nav: { name: 'Pro · Monthly', price: '$14.95 / mo', best: false },
    paid: true,
    features: [
      { type: 'on', text: 'Everything in the free account' },
      { type: 'on', text: 'Projection ranges — all 4 period finals' },
      { type: 'on', text: 'Bull & bear case + Devil’s Advocate' },
      { type: 'on', text: 'Personalized stock watchlist' },
      { type: 'on', text: 'Daily audio briefing' },
    ],
  },
  annual: {
    key: 'annual', tag: 'Pro · Annual', tagClass: 'pro-ann',
    name: 'Investment Intelligence Daily Pro', price: '$129', sub: '/ year',
    note: 'Locks in your founding rate.',
    nav: { name: 'Pro · Annual', price: '$129 / yr', best: true },
    paid: true,
    features: [
      { type: 'on', text: 'Everything in Pro monthly' },
      { type: 'on', text: 'Save $50.40 — 2 months free', icon: 'piggy' },
      { type: 'on', text: 'Lock in founding subscriber rate', icon: 'lock' },
      { type: 'on', text: 'Priority access to new features', icon: 'star' },
      { type: 'on', text: 'Daily audio briefing included', icon: 'headphones' },
    ],
  },
};

function featIcon(icon) {
  if (icon === 'piggy')      return <PiggyBank size={13} />;
  if (icon === 'lock')       return <Lock size={13} />;
  if (icon === 'star')       return <Star size={13} />;
  if (icon === 'headphones') return <Headphones size={13} />;
  return <Check size={13} strokeWidth={3} />;
}

function GoogleIcon() {
  return (
    <svg className="google-logo" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC04" d="M5.84 14.1A6.94 6.94 0 0 1 5.47 12c0-.73.13-1.44.37-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="apple-logo" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );
}

/* ── Left panel ────────────────────────────────────────────────────────── */
function RegPlanSummary({ plan, onSelectPlan }) {
  const p = PLANS[plan];
  return (
    <aside className="reg-left">
      <div className="reg-switcher">
        <div className="ps-label">Choose your plan</div>
        <div className="ps-options">
          {['free', 'monthly', 'annual'].map(k => {
            const meta = PLANS[k].nav;
            return (
              <button
                key={k}
                className={`ps-opt${plan === k ? ' active' : ''}`}
                onClick={() => onSelectPlan(k)}
                type="button"
              >
                {meta.best && <span className="ps-best">Best value</span>}
                <div>
                  <div className="ps-opt-name">{meta.name}</div>
                  <div className="ps-opt-price">{meta.price}</div>
                </div>
                {plan === k && <Check size={14} color="var(--amber)" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="reg-summary">
        <div className="psc-head">
          <div className={`psc-plan-tag ${p.tagClass}`}>
            <Sparkles size={11} /> {p.tag}
          </div>
          <div className="psc-name">{p.name}</div>
          <div className="psc-price-row">
            <span className={`psc-price${p.key === 'free' ? ' free' : ''}`}>{p.price}</span>
            <span className="psc-price-sub">{p.sub}</span>
          </div>
          <div className="psc-price-note">{p.note}</div>
        </div>
        <div className="psc-features">
          {p.features.map((f, i) => (
            <div key={i} className={`psc-feat${f.type === 'locked' ? ' locked' : ''}`}>
              {f.type === 'locked' ? <Lock size={13} /> : featIcon(f.icon)}
              {f.text}
            </div>
          ))}
        </div>
        <div className="trust-row">
          <div className="trust-item"><ShieldCheck size={12} /> 256-bit encryption</div>
          <div className="trust-item"><Lock size={12} /> Cancel anytime</div>
        </div>
      </div>

      <div className="reg-left-foot">
        <span className="dot" /> Markets open · live coverage
      </div>
    </aside>
  );
}

/* ── Step progress ─────────────────────────────────────────────────────── */
function RegStepProgress({ step, plan }) {
  const isFree = plan === 'free';
  const labels = isFree
    ? [{ k: 'auth', label: 'Account' }, { k: 'free', label: 'Confirm' }]
    : [{ k: 'auth', label: 'Account' }, { k: 'payment', label: 'Payment' }, { k: 'success', label: 'Done' }];
  const order = { auth: 0, payment: 1, free: 1, success: isFree ? 1 : 2 };
  const current = order[step] ?? 0;

  return (
    <div className="step-progress">
      {labels.map((s, i) => (
        <span key={s.k} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: i < labels.length - 1 ? 1 : 0 }}>
          <span className={`sp-item${i === current ? ' active' : ''}${i < current ? ' done' : ''}`}>
            <span className="sp-n">{i < current ? '✓' : i + 1}</span>
            {s.label}
          </span>
          {i < labels.length - 1 && <span className={`sp-line${i < current ? ' done' : ''}`} />}
        </span>
      ))}
    </div>
  );
}

/* ── Step: Auth ────────────────────────────────────────────────────────── */
function RegStepAuth({ plan, onSuccess, onSwitchToLogin }) {
  const [email, setEmail]     = useState('');
  const [pwd, setPwd]         = useState('');
  const [show, setShow]       = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  async function handleOAuth(provider) {
    setError(''); setLoading(true);
    try {
      const result = provider === 'google' ? await signInWithGoogle() : await signInWithApple();
      onSuccess(result.user);
    } catch (e) {
      setError(e.message.replace('Firebase: ', '').replace(/\(auth\/.*\)/, ''));
    } finally { setLoading(false); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const result = await registerWithEmail(email, pwd);
      onSuccess(result.user);
    } catch (e) {
      setError(e.message.replace('Firebase: ', '').replace(/\(auth\/.*\)/, ''));
    } finally { setLoading(false); }
  }

  return (
    <div className="step-body">
      <div className="step-num-tag">Step 1 — Create account</div>
      <h2 className="step-title">
        {plan === 'free' ? 'Create your free account' : 'Create your Pro account'}
      </h2>
      <p className="step-subtitle">
        {plan === 'free'
          ? "You'll get the full daily written report every market day."
          : "Next we'll process your payment securely with Stripe."}
      </p>

      <button type="button" className="auth-btn auth-btn-google" onClick={() => handleOAuth('google')} disabled={loading}>
        <GoogleIcon /> Continue with Google
      </button>
      <button type="button" className="auth-btn auth-btn-apple" onClick={() => handleOAuth('apple')} disabled={loading}>
        <AppleIcon /> Continue with Apple
      </button>

      <div className="or-divider">or with email</div>

      <form onSubmit={handleSubmit}>
        <div className="field">
          <label className="field-label" htmlFor="rm-email">Email</label>
          <div className="field-wrap">
            <span className="field-lead"><Mail size={15} /></span>
            <input
              id="rm-email" type="email" required
              className="field-input"
              placeholder="you@example.com"
              value={email} onChange={e => setEmail(e.target.value)}
            />
          </div>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="rm-pwd">Password</label>
          <div className="field-wrap">
            <span className="field-lead"><Lock size={15} /></span>
            <input
              id="rm-pwd" type={show ? 'text' : 'password'} required minLength={8}
              className="field-input field-input--trail"
              placeholder="At least 8 characters"
              value={pwd} onChange={e => setPwd(e.target.value)}
            />
            <button type="button" className="field-trail" onClick={() => setShow(s => !s)} aria-label="Toggle password">
              {show ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <div className="field-hint">8+ characters · 1 number recommended</div>
        </div>
        {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <button type="submit" className="btn-main" disabled={loading}>
          {plan === 'free'
            ? <><UserPlus size={16} /> {loading ? 'Creating…' : 'Create free account'}</>
            : <>{loading ? 'Creating account…' : 'Continue to payment'} <ArrowRight size={16} /></>}
        </button>
      </form>

      <div className="existing-account">
        Already have an account?{' '}
        <a href="#" onClick={e => { e.preventDefault(); onSwitchToLogin?.(); }}>Sign in</a>
      </div>
    </div>
  );
}

/* ── Step: Payment ─────────────────────────────────────────────────────── */
function RegStepPayment({ plan, user, onBack }) {
  const p = PLANS[plan];
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function handlePay() {
    setLoading(true); setError('');
    try {
      await redirectToCheckout(plan);
    } catch {
      setError('Could not start checkout. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="step-body">
      {onBack && <button className="btn-back" onClick={onBack}><ArrowLeft size={14} /> Back</button>}
      <div className="step-num-tag">Step 2 — Payment</div>
      <h2 className="step-title">Confirm &amp; pay</h2>
      <p className="step-subtitle">
        Pay {p.price} {p.sub} — secured by Stripe.
        {user?.email && <> Logged in as <strong style={{ color: 'var(--ink)' }}>{user.email}</strong>.</>}
      </p>

      <div className="stripe-card">
        <div className="stripe-card-head">
          <div>
            <div className="stripe-order-label">{p.tag}</div>
            <div className="stripe-order-name">{p.name}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="stripe-order-price">{p.price}</div>
            <div className="stripe-order-period">{p.sub}</div>
          </div>
        </div>
        <div className="stripe-body" style={{ textAlign: 'center' }}>
          <Lock size={16} style={{ color: 'var(--stripe)', marginBottom: 8 }} />
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>
            You'll be redirected to Stripe's secure checkout to complete payment.
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
            We never store your card details. Powered by{' '}
            <strong style={{ color: 'var(--stripe)' }}>Stripe</strong>.
          </div>
        </div>
      </div>

      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>{error}</div>}

      <button className="stripe-pay-btn" onClick={handlePay} disabled={loading}>
        <Lock size={15} /> {loading ? 'Redirecting to Stripe…' : `Pay ${p.price} ${p.sub}`}
      </button>
      <div className="stripe-security">
        <ShieldCheck size={12} /> Encrypted &amp; PCI-compliant · cancel anytime
      </div>
    </div>
  );
}

/* ── Step: Free confirm ────────────────────────────────────────────────── */
function RegStepFree({ user, onUpgrade, onSuccess }) {
  return (
    <div className="step-body">
      <div className="step-num-tag">Step 2 — All set</div>
      <h2 className="step-title">You're in</h2>
      <p className="step-subtitle">
        {user?.email ? `Welcome, ${user.email}.` : 'Welcome.'} Your free account is ready.
      </p>

      <div className="free-confirm">
        <Check size={18} strokeWidth={3} />
        <div>
          <div className="fc-title">Free account active</div>
          <div className="fc-text">
            You'll get the full daily written report every market day, plus Hit / Beat / Miss outcomes.
          </div>
        </div>
      </div>

      <div className="upgrade-card">
        <div className="ic"><Zap size={16} /></div>
        <div className="tx">
          <b>Unlock projection ranges &amp; bull/bear cases</b>
          <span>Pro Annual — $129 / yr · save 2 months</span>
        </div>
        <button onClick={onUpgrade}>Upgrade</button>
      </div>

      <button className="btn-main" style={{ marginTop: 16 }} onClick={onSuccess}>
        Open today's report <ArrowRight size={16} />
      </button>
    </div>
  );
}

/* ── Step: Success ─────────────────────────────────────────────────────── */
function RegStepSuccess({ plan, onClose }) {
  const p = PLANS[plan];
  return (
    <div className="step-body">
      <div className="success-icon"><Check size={28} strokeWidth={3} /></div>
      <h2 className="success-title">You're all set</h2>
      <p className="success-sub">
        Your {p.tag} is active. Time to read today's intelligence.
      </p>
      <div className="success-meta">
        <div className="success-meta-row">
          <div className="l">Plan</div>
          <div className="v">{p.tag}</div>
        </div>
        <div className="success-meta-row" style={{ textAlign: 'right' }}>
          <div className="l">Billed</div>
          <div className="v">{p.price}{p.paid ? ` ${p.sub}` : ''}</div>
        </div>
      </div>
      <button className="btn-main gold" onClick={onClose}>
        <Sparkles size={16} /> Open today's report
      </button>
    </div>
  );
}

/* ── Modal root ────────────────────────────────────────────────────────── */
export default function RegisterModal({ onClose, initialPlan = 'annual', initialStep = 'auth', initialUser = null, onSwitchToLogin }) {
  const navigate = useNavigate();
  const [plan, setPlan]         = useState(initialPlan);
  const [step, setStep]         = useState(initialStep);
  const [authUser, setAuthUser] = useState(initialUser);

  const close = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  function handlePlanSelect(p) {
    setPlan(p);
    if (step !== 'auth' && !authUser) setStep('auth');
  }
  function handleAuthSuccess(user) {
    setAuthUser(user);
    setStep(plan === 'free' ? 'free' : 'payment');
  }
  function handleUpgrade()     { setPlan('annual'); setStep('payment'); }
  function handleFreeSuccess() { close(); navigate('/platform'); }
  function handleDone()        { close(); navigate('/platform'); }

  return (
    <div className="reg-overlay" onClick={e => { if (e.target === e.currentTarget) close(); }}>
      <div className="reg-modal" role="dialog" aria-modal="true" aria-label="Create account">
        <RegPlanSummary plan={plan} onSelectPlan={handlePlanSelect} />

        <div className="reg-step-panel">
          <button className="reg-modal-close" onClick={close} aria-label="Close">
            <X size={15} />
          </button>

          <RegStepProgress step={step} plan={plan} />

          {step === 'auth' && (
            <RegStepAuth plan={plan} onSuccess={handleAuthSuccess}
              onSwitchToLogin={onSwitchToLogin ?? close} />
          )}
          {step === 'payment' && (
            <RegStepPayment plan={plan} user={authUser} onBack={initialUser ? null : () => setStep('auth')} />
          )}
          {step === 'free' && (
            <RegStepFree user={authUser} onUpgrade={handleUpgrade} onSuccess={handleFreeSuccess} />
          )}
          {step === 'success' && (
            <RegStepSuccess plan={plan} onClose={handleDone} />
          )}
        </div>
      </div>
    </div>
  );
}
